import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  DialogContentText,
} from '@mui/material';
import {
  Check,
  Close,
  Person,
  Lock,
  Block,
  CheckCircle,
  Add,
  Delete,
  Refresh,
  Warning,
  Security,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

interface AccessRequest {
  id: number;
  user_name: string;
  user_email: string;
  page_name: string;
  page_display_name: string;
  message: string;
  created_at: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  page_access: Array<{ page_name: string; granted_at: string }>;
}

const AVAILABLE_PAGES = [
  { name: 'dashboard', display: 'Dashboard' },
  { name: 'portfolio', display: 'Portfolio Management' },
  { name: 'people', display: 'People Metrics' },
  { name: 'map', display: 'Interactive Map' },
  { name: 'methodology', display: 'Methodology' },
  { name: 'monitor', display: 'Supply Chain Monitor' },
];

export default function AdminDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  // Approval Dialog State
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  // Manage User Permissions Dialog State
  const [managePermissionsOpen, setManagePermissionsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);

  // Create User Dialog State
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    pages: [] as string[]
  });

  // Password Dialog State
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    confirmColor: 'primary' as 'primary' | 'error' | 'warning'
  });

  useEffect(() => {
    fetchRequests();
    fetchUsers();
  }, []);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/permissions/admin/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Fetch requests error:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/permissions/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Fetch users error:', error);
    }
  };

  // Confirmation Dialog Helper
  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    confirmText: string = 'Confirm',
    confirmColor: 'primary' | 'error' | 'warning' = 'primary'
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText,
      confirmColor
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false
    });
  };

  const handleConfirm = () => {
    confirmDialog.onConfirm();
    closeConfirmDialog();
  };

  // ===== Access Request Handlers =====
  const handleOpenApprovalDialog = (request: AccessRequest) => {
    setSelectedRequest(request);
    setSelectedPages([request.page_name]);
    setApprovalDialogOpen(true);
  };

  const handlePageToggle = (pageName: string) => {
    setSelectedPages(prev =>
      prev.includes(pageName)
        ? prev.filter(p => p !== pageName)
        : [...prev, pageName]
    );
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/permissions/admin/review-request`,
        {
          requestId: selectedRequest.id,
          status: 'approved',
          pages: selectedPages
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setApprovalDialogOpen(false);
      fetchRequests();
      fetchUsers();
      alert(`Access granted to ${selectedPages.length} page(s)`);
    } catch (error: any) {
      console.error('Approve error:', error);
      alert(error.response?.data?.error || 'Failed to approve request');
    }
  };

  const handleReject = async (requestId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/permissions/admin/review-request`,
        {
          requestId,
          status: 'rejected',
          pages: []
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      fetchRequests();
      alert('Request rejected');
    } catch (error: any) {
      console.error('Reject error:', error);
      alert(error.response?.data?.error || 'Failed to reject request');
    }
  };

  // ===== NEW: Manage User Permissions Handlers =====
  const handleOpenManagePermissions = (user: User) => {
    setSelectedUser(user);
    // Set current permissions
    const currentPages = user.page_access.map(p => p.page_name);
    setUserPermissions(currentPages);
    setManagePermissionsOpen(true);
  };

  const handlePermissionToggle = (pageName: string) => {
    setUserPermissions(prev =>
      prev.includes(pageName)
        ? prev.filter(p => p !== pageName)
        : [...prev, pageName]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get current permissions
      const currentPages = selectedUser.page_access.map(p => p.page_name);
      
      // Determine which pages to grant and which to revoke
      const pagesToGrant = userPermissions.filter(p => !currentPages.includes(p));
      const pagesToRevoke = currentPages.filter(p => !userPermissions.includes(p));

      // Grant new permissions
      if (pagesToGrant.length > 0) {
        await axios.post(
          `${API_URL}/permissions/admin/grant-access`,
          {
            userId: selectedUser.id,
            pages: pagesToGrant
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Revoke removed permissions
      for (const pageName of pagesToRevoke) {
        await axios.delete(
          `${API_URL}/permissions/admin/revoke-access/${selectedUser.id}/${pageName}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      setManagePermissionsOpen(false);
      fetchUsers();
      alert('Permissions updated successfully');
    } catch (error: any) {
      console.error('Save permissions error:', error);
      alert(error.response?.data?.error || 'Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  // ===== Create User Handlers =====
  const handleOpenCreateUser = () => {
    setNewUser({
      name: '',
      email: '',
      password: '',
      role: 'user',
      pages: []
    });
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.name || !newUser.email || !newUser.password) {
        alert('Please fill in all required fields');
        return;
      }

      if (newUser.password.length < 6) {
        alert('Password must be at least 6 characters');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/permissions/admin/create-user`,
        newUser,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setCreateUserDialogOpen(false);
      fetchUsers();
      alert('User created successfully');
    } catch (error: any) {
      console.error('Create user error:', error);
      alert(error.response?.data?.error || 'Failed to create user');
    }
  };

  // ===== Password Change Handlers =====
  const handleOpenPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;

    try {
      if (newPassword.length < 6) {
        alert('Password must be at least 6 characters');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/permissions/admin/change-password`,
        {
          userId: selectedUser.id,
          newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPasswordDialogOpen(false);
      alert('Password changed successfully');
    } catch (error: any) {
      console.error('Change password error:', error);
      alert(error.response?.data?.error || 'Failed to change password');
    }
  };

  // ===== Suspend/Activate Handlers =====
  const handleToggleUserStatus = (userId: number, currentStatus: string, userName: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'suspended' ? 'suspend' : 'activate';

    showConfirmDialog(
      `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      `Are you sure you want to ${action} ${userName}?`,
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API_URL}/permissions/admin/toggle-user-status`,
            {
              userId,
              status: newStatus
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          fetchUsers();
          alert(`User ${action}d successfully`);
        } catch (error: any) {
          console.error('Toggle status error:', error);
          alert(error.response?.data?.error || 'Failed to update user status');
        }
      },
      action.charAt(0).toUpperCase() + action.slice(1),
      newStatus === 'suspended' ? 'warning' : 'primary'
    );
  };

  // ===== Delete User Handler =====
  const handleDeleteUser = (userId: number, userName: string) => {
    showConfirmDialog(
      'Delete User',
      `Are you sure you want to permanently delete "${userName}"? This action cannot be undone.`,
      async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(
            `${API_URL}/permissions/admin/delete-user/${userId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          fetchUsers();
          alert('User deleted successfully');
        } catch (error: any) {
          console.error('Delete user error:', error);
          alert(error.response?.data?.error || 'Failed to delete user');
        }
      },
      'Delete',
      'error'
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreateUser}
            sx={{ mr: 1 }}
          >
            Create User
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchRequests();
              fetchUsers();
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`Pending Requests (${requests.length})`} />
          <Tab label={`All Users (${users.length})`} />
        </Tabs>
      </Paper>

      {/* ===== PENDING REQUESTS TAB ===== */}
      {tabValue === 0 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Page Requested</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No pending requests
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.user_name}</TableCell>
                      <TableCell>{request.user_email}</TableCell>
                      <TableCell>
                        <Chip label={request.page_display_name} size="small" />
                      </TableCell>
                      <TableCell>{request.message || '-'}</TableCell>
                      <TableCell>
                        {new Date(request.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          startIcon={<Check />}
                          color="success"
                          size="small"
                          onClick={() => handleOpenApprovalDialog(request)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          startIcon={<Close />}
                          color="error"
                          size="small"
                          onClick={() => handleReject(request.id)}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ===== ALL USERS TAB ===== */}
      {tabValue === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Page Access</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'secondary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={user.status === 'active' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.page_access.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No access
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {user.page_access.map((access) => (
                            <Chip
                              key={access.page_name}
                              label={AVAILABLE_PAGES.find(p => p.name === access.page_name)?.display || access.page_name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {/* NEW: Manage Permissions Button */}
                      <Tooltip title="Manage Permissions">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenManagePermissions(user)}
                        >
                          <Security />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Change Password">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenPasswordDialog(user)}
                        >
                          <Lock />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.status === 'active' ? 'Suspend User' : 'Activate User'}>
                        <IconButton
                          size="small"
                          color={user.status === 'active' ? 'warning' : 'success'}
                          onClick={() => handleToggleUserStatus(user.id, user.status, user.name)}
                        >
                          {user.status === 'active' ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteUser(user.id, user.name)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ===== CONFIRMATION DIALOG ===== */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color={confirmDialog.confirmColor} />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained" 
            color={confirmDialog.confirmColor}
          >
            {confirmDialog.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== APPROVAL DIALOG ===== */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Page Access</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select which pages to grant access to for <strong>{selectedRequest?.user_name}</strong>
          </Alert>
          <FormGroup>
            {AVAILABLE_PAGES.map((page) => (
              <FormControlLabel
                key={page.name}
                control={
                  <Checkbox
                    checked={selectedPages.includes(page.name)}
                    onChange={() => handlePageToggle(page.name)}
                  />
                }
                label={page.display}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== NEW: MANAGE USER PERMISSIONS DIALOG ===== */}
      <Dialog open={managePermissionsOpen} onClose={() => setManagePermissionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Permissions for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select which pages <strong>{selectedUser?.name}</strong> can access
          </Alert>
          <FormGroup>
            {AVAILABLE_PAGES.map((page) => (
              <FormControlLabel
                key={page.name}
                control={
                  <Checkbox
                    checked={userPermissions.includes(page.name)}
                    onChange={() => handlePermissionToggle(page.name)}
                  />
                }
                label={page.display}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagePermissionsOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePermissions} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== CREATE USER DIALOG ===== */}
      <Dialog open={createUserDialogOpen} onClose={() => setCreateUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  helperText="Minimum 6 characters"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    label="Role"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Grant Access to Pages (optional):
                </Typography>
                <FormGroup>
                  {AVAILABLE_PAGES.map((page) => (
                    <FormControlLabel
                      key={page.name}
                      control={
                        <Checkbox
                          checked={newUser.pages.includes(page.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewUser({ ...newUser, pages: [...newUser.pages, page.name] });
                            } else {
                              setNewUser({ ...newUser, pages: newUser.pages.filter(p => p !== page.name) });
                            }
                          }}
                        />
                      }
                      label={page.display}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== PASSWORD CHANGE DIALOG ===== */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password for {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Minimum 6 characters"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChangePassword} variant="contained" color="primary">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}